import { useEffect } from 'react';
import { getHuckConfig } from '../../utils/getHuckConfig.js';

declare global {
  interface Window {
    hj?: (type: 'event', eventName: string) => void;
  }
}

const brandToHotjarIdMap = {
  FINN: '1666135',
  DBA: '2671766',
  BLOCKET: null,
  BILBASEN: null,
};

function hotjar({ snippetVersion = 6 } = {}) {
  const { brand, canUseHotjar } = getHuckConfig();
  const hotjarId = brandToHotjarIdMap[brand];

  // 1. Only run in browser environment
  // 2. Only run if user has consented to analytics and the Hotjar feature toggle is enabled
  // NOTE: The consents are only important to be fully respected for use of Hotjar recordings
  if (typeof window === 'undefined' || typeof document === 'undefined' || !canUseHotjar || !hotjarId) {
    return Promise.resolve();
  }

  const snippet = `
  (function (h, o, t, j, a, r) {
    h.hj =
      h.hj ||
      function () {
        (h.hj.q = h.hj.q || []).push(arguments);
      };
    h._hjSettings = {
      hjid: ${hotjarId}, // Hotjar ID
      hjsv: ${snippetVersion}, // Hotjar Snippet Version
    };
    a = o.getElementsByTagName('head')[0];
    r = o.createElement('script');
    r.async = 1;
    r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
    a.appendChild(r);
  })(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');
  `;

  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.text = snippet;
    document.head.appendChild(script);
    // give the script some time to load before we start using it
    setTimeout(resolve, 3000);
  });
}

const hotjarIsLoaded = () => {
  return typeof window !== 'undefined' && typeof window.hj === 'function';
};

export const triggerSurveyEvent = (eventName: string) => {
  const triggerFn = () => window.hj?.('event', eventName);
  if (hotjarIsLoaded()) {
    triggerFn();
  } else {
    console.warn(`Hotjar is not loaded, cannot trigger event: ${eventName}`);
  }
};

export const useHotjar = async () => {
  useEffect(() => {
    if (!hotjarIsLoaded()) {
      hotjar()
        .then(() => {
          console.log('Hotjar script loaded successfully');
        })
        .catch((error) => {
          console.error('Error loading Hotjar script:', error);
        });
    }
  }, []);
};
